import { useEffect, useRef } from "react";

const TICKS = Array.from({ length: 21 }, (_, i) => {
  const v = i * 0.5;
  const major = Number.isInteger(v);
  const th = ((225 - 27 * v) * Math.PI) / 180;
  const rOut = 176;
  const rIn = major ? 146 : 158;
  return {
    v,
    major,
    x1: 200 + rIn * Math.cos(th),
    y1: 200 - rIn * Math.sin(th),
    x2: 200 + rOut * Math.cos(th),
    y2: 200 - rOut * Math.sin(th),
    color: v >= 8 ? "#D97706" : "#1B365D",
  };
});

const NUMBERS = Array.from({ length: 11 }, (_, v) => {
  const th = ((225 - 27 * v) * Math.PI) / 180;
  return { v, x: 200 + 114 * Math.cos(th), y: 200 - 114 * Math.sin(th) };
});

export default function Manometer() {
  const needleRef = useRef(null);
  const readoutRef = useRef(null);

  useEffect(() => {
    const mouse = { x: 0, y: 0, speed: 0, px: null, py: null };
    const onMove = (e) => {
      mouse.x = (e.clientX / window.innerWidth) * 2 - 1;
      mouse.y = (e.clientY / window.innerHeight) * 2 - 1;
      if (mouse.px !== null) {
        mouse.speed = Math.min(1, mouse.speed + Math.abs(mouse.x - mouse.px) * 1.6 + Math.abs(mouse.y - mouse.py) * 1.6);
      }
      mouse.px = mouse.x;
      mouse.py = mouse.y;
    };
    window.addEventListener("pointermove", onMove);

    let raf = 0;
    let frame = 0;
    let pressure = 0;
    let lastScrollY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      mouse.speed = Math.min(1, mouse.speed + Math.abs(y - lastScrollY) * 0.004);
      lastScrollY = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    const start = performance.now();
    const animate = (now) => {
      raf = requestAnimationFrame(animate);
      const el = (now - start) / 1000;
      const intro = Math.min(1, el / 2.2);
      const ease = 1 - Math.pow(1 - intro, 3);
      mouse.speed *= 0.94;

      const target = Math.min(10, Math.max(0, ease * (2.4 + Math.sin(el * 1.7) * 0.7 + mouse.speed * 6.5)));
      pressure += (target - pressure) * 0.08;

      if (needleRef.current) {
        needleRef.current.setAttribute("transform", `rotate(${27 * pressure - 225} 200 200)`);
      }
      frame += 1;
      if (readoutRef.current && frame % 6 === 0) {
        readoutRef.current.textContent = `${pressure.toFixed(1).replace(".", ",")} bar`;
      }
    };
    raf = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  return (
    <div className="relative flex h-[320px] w-full items-center justify-center sm:h-[430px] lg:h-[540px]" data-testid="hidropro-hero-manometer">
      <svg viewBox="0 0 400 400" className="h-[78%] w-auto" role="img" aria-label="Manômetro HidroPro">
        <circle cx="200" cy="200" r="192" fill="#FFFFFF" stroke="#0A5C8C" strokeWidth="11" />

        <path d="M 313 313 A 160 160 0 0 0 358 175" fill="none" stroke="#D97706" strokeWidth="12" strokeLinecap="butt" opacity="0.9" />

        {TICKS.map((t) => (
          <line key={t.v} x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2} stroke={t.color} strokeWidth={t.major ? 5 : 2.5} strokeLinecap="round" />
        ))}
        {NUMBERS.map((n) => (
          <text key={n.v} x={n.x} y={n.y} textAnchor="middle" dominantBaseline="central" fill="#1B365D" fontFamily="'JetBrains Mono', monospace" fontWeight="700" fontSize="25">
            {n.v}
          </text>
        ))}

        <text x="200" y="122" textAnchor="middle" fill="#0A5C8C" fontFamily="'JetBrains Mono', monospace" fontWeight="700" fontSize="19" letterSpacing="3">
          HIDROPRO
        </text>
        <text x="200" y="290" textAnchor="middle" fill="#4A5568" fontFamily="'IBM Plex Sans', sans-serif" fontWeight="500" fontSize="24">
          bar
        </text>

        <g ref={needleRef} transform="rotate(-225 200 200)">
          <line x1="182" y1="200" x2="368" y2="200" stroke="#D97706" strokeWidth="9" strokeLinecap="round" />
        </g>
        <circle cx="200" cy="200" r="14" fill="#0A5C8C" />
        <circle cx="200" cy="200" r="5" fill="#B87333" />
      </svg>

      <div className="pointer-events-none absolute bottom-3 left-3 rounded-xl border border-[rgba(10,92,140,0.25)] bg-white/85 px-4 py-2.5 backdrop-blur sm:bottom-5 sm:left-5">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#0A5C8C]">Demonstração de pressão</div>
        <div className="font-display text-lg font-bold text-[#1A1A1A]" data-testid="hidropro-pressure-readout" ref={readoutRef}>
          0,0 bar
        </div>
      </div>

      <div className="pointer-events-none absolute right-4 top-4 hidden font-mono text-[10px] uppercase tracking-[0.2em] text-[#0A5C8C]/70 sm:block">
        Simulação ilustrativa de um sistema hidráulico.
      </div>
    </div>
  );
}
